// dbManager.ts
import Dexie from "dexie";

export interface ColumnDefinition {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  isPrimaryKey?: boolean;
}

let dbInstance: Dexie | null = null;
let initPromise: Promise<Dexie> | null = null;

function hashSchema(obj: any): string {
  const sortedObj = Object.keys(obj).sort().reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {} as typeof obj);
  return JSON.stringify(sortedObj);
}


export async function prepareDexieSchema(
  allTableSchemas: Record<string, ColumnDefinition[]>,
  forceVersionUpdate = false
): Promise<Dexie> {
  if (dbInstance && dbInstance.isOpen() && !forceVersionUpdate) return dbInstance;
  
  if (dbInstance) {
    try {
      dbInstance.close();
    } catch {
      // ignore
    }
    dbInstance = null;
  }

  const stores: Record<string, string> = {};
  for (const [tableName, columns] of Object.entries(allTableSchemas)) {
    const primaryKeys = columns.filter(col => col.isPrimaryKey).map(col => col.name);
    const otherKeys = columns.filter(col => !col.isPrimaryKey).map(col => col.name);
    
    // Force these fields to be indexed for specific tables to prevent runtime errors
    const mandatoryIndices = (tableName === 'Position' || tableName === 'Employee') 
      ? ['OrgUnitId', 'PositionId', 'EmployeeName', 'EmployeeCode'] 
      : [];
      
    const schemaStr = [...new Set([...primaryKeys, ...otherKeys, ...mandatoryIndices])].join(',') || '++id';
    stores[tableName] = schemaStr;
  }

  const schemaHash = JSON.stringify(stores);
  const previousHash = localStorage.getItem('dexie_schema_hash');
  let version = parseInt(localStorage.getItem('dexie_dynamic_version') || '1', 10);

  if (forceVersionUpdate || schemaHash !== previousHash) {
    version += 1;
    localStorage.setItem('dexie_dynamic_version', version.toString());
    localStorage.setItem('dexie_schema_hash', schemaHash);
    console.warn('🔁 Dexie version bump: Schema changed or forced update (v' + version + ').');
  }

  const db = new Dexie('DynamicDB');
  db.version(version).stores(stores);

  try {
    await db.open();
    dbInstance = db;
    console.log('🔁 Dexie schema prepared, version: ' + version);
    return db;
  } catch (err) {
    console.warn('⚠️ Error opening Dexie DynamicDB, attempting version bump recovery:', err);
    try {
      db.close();
      version += 1;
      localStorage.setItem('dexie_dynamic_version', version.toString());
      const recoveryDb = new Dexie('DynamicDB');
      recoveryDb.version(version).stores(stores);
      await recoveryDb.open();
      dbInstance = recoveryDb;
      console.log('✅ Dexie recovered successfully with version: ' + version);
      return recoveryDb;
    } catch (recoveryErr) {
      console.error('❌ Dexie recovery failed:', recoveryErr);
      if (localStorage.getItem('isLoggedIn') === 'true' && window.location.pathname !== '/firstLoader') {
        window.location.href = '/firstLoader';
      }
      throw recoveryErr;
    }
  }
}

export async function getDB(): Promise<Dexie> {
  if (!dbInstance || !dbInstance.isOpen()) {
    await ensureDexieReady();
  }
  if (!dbInstance) {
    throw new Error('⚠️ Dexie DB not initialized. Call prepareDexieSchema() first.');
  }
  return dbInstance;
}
// dexieHelpers.ts

export async function insertDataToDexie<T>(
  tableName: string,
  data: T[]
) {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn(`⚠️ No data to insert for ${tableName}`);
    return;
  }

  const db = await getDB();
  await db.table<T, any>(tableName).bulkPut(data);
  console.log(`✅ Inserted ${data.length} records into "${tableName}"`);
}

export async function getDataFromDexie(tableName: string) {
  const db = await getDB();
  const data = await db.table(tableName).toArray();
  console.log(`📦 Retrieved ${data.length} records from ${tableName}`);
  return data;
}

export async function getDataFromDexiebyField(
  tableName: string,
  whereClause?: { field: string; value: any }
) {
  const db = await getDB();
  const table = db.table(tableName);

  if (whereClause) {
    const { field, value } = whereClause;

    if (value === undefined || value === null ||
        (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date) && !Array.isArray(value))) {
      console.warn(`⚠️ Invalid key provided for table ${tableName}:`, whereClause);
      return [];
    }

    console.log(`🔍 Querying ${tableName} with field: ${field}, value: ${value}`);
    return table.where(field).equals(value).toArray();
  }
  return table.toArray();
}

export async function getFieldValuesFromDexie(
  tableName: string,
  fieldName: string
): Promise<string[]> {
  const db = await getDB();
  const table = db.table(tableName);
  const data = await table.toArray();

  return data
    .map((item: Record<string, any>) => item[fieldName])
    .filter((val) => val !== undefined && val !== null)
    .map((val) => String(val));
}

export async function getDataFromDexieArray(
  tableName: string,
  pk: string,
  whereClause?: { field: string; value: any }
): Promise<any[]> {
  const db = await getDB();
  const table = db.table(tableName);

  const rows = whereClause
    ? await table.where(whereClause.field).equals(whereClause.value).toArray()
    : await table.toArray();

  const result: any[] = [];
  rows.forEach(row => {
    result[row[pk]] = row;
  });

  return result;
}
export async function ensureDexieReady(): Promise<Dexie | void> {
  if (dbInstance && dbInstance.isOpen()) return dbInstance;
  if (initPromise) return initPromise;

  const existing = localStorage.getItem('dexie_all_schema');
  if (existing) {
    try {
      const schemas = JSON.parse(existing);
      initPromise = prepareDexieSchema(schemas).finally(() => {
        initPromise = null;
      });
      return initPromise;
    } catch (e) {
      console.error('Failed to parse dexie_all_schema:', e);
    }
  } else {
    console.warn('❌ No saved schema found in localStorage');
    if (localStorage.getItem('isLoggedIn') === 'true' && window.location.pathname !== '/firstLoader') {
      window.location.href = '/firstLoader';
    }
  }
}

export async function getActivePermKeysFromDexie(): Promise<string[]> {
  try {
    if (!dbInstance || !dbInstance.isOpen()) {
      await ensureDexieReady();
    }

    const db = await getDB();
    
    // Safeguard: Check if table exists before accessing it
    if (!db.tables.find(t => t.name === 'RolePerms')) {
      console.warn('⚠️ Table RolePerms does not exist. Returning empty permissions.');
      return [];
    }

    const table = db.table('RolePerms');
    const rows = await table.toArray();

    const permKeys: string[] = rows
      .filter(row => row.PermKey && row.isActive)
      .map(row => row.PermKey);

    return permKeys;
  } catch (error) {
    console.warn('⚠️ Could not load active perm keys from Dexie:', error);
    return [];
  }
}

export async function getFilteredPositionIds(
  tableName: string,
  orgUnitIdValue: any
): Promise<string[]> {
  if (!dbInstance) await ensureDexieReady();
  const db = await getDB();
  const table = db.table(tableName);

  // 🛡️ Robust Check: Verify if OrgUnitId is actually indexed before using .where()
  const isIndexed = table.schema.indexes.some(idx => idx.name === 'OrgUnitId');

  let data;
  if (isIndexed && orgUnitIdValue !== undefined && orgUnitIdValue !== null) {
    data = await table
      .where('OrgUnitId')
      .equals(orgUnitIdValue)
      .toArray();
  } else {
    console.warn(`⚠️ Field 'OrgUnitId' query fallback. isIndexed: ${isIndexed}, value: ${orgUnitIdValue}`);
    data = await table.toArray();
    if (orgUnitIdValue !== undefined && orgUnitIdValue !== null) {
      data = data.filter(item => String(item.OrgUnitId) === String(orgUnitIdValue));
    } else {
      // If no unitId provided, maybe return empty or all? 
      // Usually, we want all if we don't filter, or none if we expect a filter.
      // Given the context of reports, return empty to be safe or all?
      // Let's return all but log it.
      console.warn("⚠️ No orgUnitIdValue provided to getFilteredPositionIds. Returning all records.");
    }
  }
    // console.log(data, 'Filtered Position IDs');

  return data
    .map((item: Record<string, any>) => item['PositionId'])
    .filter((val) => val !== undefined && val !== null)
    .map((val) => String(val));
}
